import React from 'react';
import {
  BookOpenIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { resolveCitedSourceLinks, resolveExternalToolSourceDetails } from '../../lib/sources';

/**
 * Bibliography section: cited knowledge-base sources, parsed references and NCBI sources.
 * Extracted verbatim from pages/Articles/Article.js.
 */
export default function ArticleBibliography({
  availableKnowledgeBaseSources,
  citedKnowledgeBaseSources,
  contentType,
  externalToolEvidence,
  extractedReferences,
  getUniqueFilenames,
  handleReferenceClick,
  linkedSources,
  totalReferences,
  uniqueFiles,
}) {
  const { t } = useTranslation();

  return (
    <section id="bibliography" className="scroll-mt-20">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-2xl font-medium mb-6 text-gray-800">
          {t('articles.editor.tabs.bibliography')}{citedKnowledgeBaseSources.length > 0 || totalReferences > 0 || linkedSources.length > 0
            ? ` (${citedKnowledgeBaseSources.length || uniqueFiles + linkedSources.length})`
            : ''}
        </h2>

        {citedKnowledgeBaseSources.length > 0 ? (
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-medium text-gray-700">{t('articles.editor.bibliography.citedInArticle')}</h3>
            <div className="grid gap-3">
              {citedKnowledgeBaseSources.map((source, index) => {
                const externalDetails = resolveExternalToolSourceDetails(source, externalToolEvidence);
                const displayTitle = source.title
                  || externalDetails?.datasetName
                  || source.filename
                  || t('articles.editor.bibliography.sourceNumber', { number: index + 1 });
                const displayFilename = source.filename || source.type || t('articles.editor.bibliography.vectorStoreFile');
                const { sourceUrl, apiPreviewUrl, queryText } = resolveCitedSourceLinks(source, externalDetails);
                return (
                  <div
                    key={`${source.file_id || source.filename || index}`}
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-blue-600 shrink-0 font-medium text-sm">[{index + 1}]</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 break-words">{displayTitle}</div>
                        <div className="text-xs text-gray-500 mt-1 break-all">{displayFilename}</div>
                        {externalDetails?.toolNameLabel && (
                          <div className="text-xs text-gray-500 mt-1 break-words">
                            Narzedzie: {externalDetails.toolNameLabel}
                          </div>
                        )}
                        {queryText && (
                          <div className="text-xs text-gray-500 mt-1 break-words">
                            Fraza: {queryText}
                          </div>
                        )}
                        {sourceUrl && (
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-500 hover:text-primary-600 mt-1 inline-block"
                          >
                            {t('articles.editor.bibliography.viewSource')}
                          </a>
                        )}
                        {apiPreviewUrl && (
                          <a
                            href={apiPreviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-500 hover:text-primary-600 mt-1 ml-3 inline-block"
                          >
                            Podglad zapytania API
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : availableKnowledgeBaseSources.length > 0 ? (
          <div className="text-sm text-gray-500 mb-6">
            {t('articles.editor.bibliography.noCitationsDetected')}
          </div>
        ) : null}

        {availableKnowledgeBaseSources.length > 0 && citedKnowledgeBaseSources.length === 0 && (
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-medium text-gray-700">{t('articles.editor.bibliography.availableNotCited')}</h3>
            <div className="grid gap-3">
              {availableKnowledgeBaseSources.map((source, index) => {
                const displayTitle = source.title || source.filename || t('articles.editor.bibliography.sourceNumber', { number: index + 1 });
                const displayFilename = source.filename || source.type || t('articles.editor.bibliography.vectorStoreFile');
                return (
                  <div
                    key={`available-${source.file_id || source.filename || index}`}
                    className="bg-neutral-50 p-4 rounded-lg border border-dashed border-gray-300"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-gray-400 shrink-0 font-medium text-sm">—</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700 break-words">{displayTitle}</div>
                        <div className="text-xs text-gray-500 mt-1 break-all">{displayFilename}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {totalReferences > 0 ? (
          <div className="space-y-4">


            {/* Referenced Files List */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">{t('articles.editor.bibliography.referencedSources')}</h3>
              <div className="grid gap-3">
                {/* Check if this is Article Summary with linkedSources - show real article info instead of fake "Vector Store Source 1" */}
                {contentType === 'summary' && linkedSources.length > 0 && extractedReferences.some(r => r.type === 'vector_store') ? (
                  // Show real linked sources for Article Summary
                  linkedSources.map((source, index) => (
                    <div
                      key={source.id || index}
                      className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => {
                        if (source.article_link) {
                          window.open(source.article_link, '_blank');
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-blue-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {source.article_title || 'Untitled Source'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {source.article_author ? `${source.article_author} • ` : ''}
                              NCBI Scientific Article
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {source.article_link ? 'View on NCBI →' : 'NCBI Source'}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Show regular parsed references for other article types
                  getUniqueFilenames().map((filename) => {
                    const fileRefs = extractedReferences.filter(r => r.filename === filename);
                    const firstRef = fileRefs[0];

                    // Handle different reference types for display
                    let displayName = filename;
                    let displayDescription = `${fileRefs.length} reference${fileRefs.length !== 1 ? 's' : ''}`;
                    let displayIcon = <PaperClipIcon className="w-5 h-5" />;

                    // Vector store references (fallback for cases without linkedSources)
                    if (firstRef?.type === 'vector_store' || firstRef?.type === 'vector_store_prefixed' || filename.includes('Vector Store')) {
                      displayName = firstRef.displayText || `Vector Store Source ${firstRef.sourceId}`;
                      displayDescription = 'OpenAI Knowledge Base';
                      displayIcon = <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" /></svg>;
                    }
                  // Simple numbered references
                  else if (firstRef?.type === 'simple_numbered' && filename.startsWith('Source ')) {
                    displayName = firstRef.displayText || filename;
                    displayDescription = 'Reference Source';
                    displayIcon = <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
                  }
                  // Regular file types
                  else {
                    if (filename.includes('.json')) {
                      displayIcon = <DocumentTextIcon className="w-5 h-5" />;
                      displayDescription = `JSON File • ${fileRefs.length} reference${fileRefs.length !== 1 ? 's' : ''}`;
                    } else if (filename.includes('.txt')) {
                      displayIcon = <DocumentIcon className="w-5 h-5" />;
                      displayDescription = `Text File • ${fileRefs.length} reference${fileRefs.length !== 1 ? 's' : ''}`;
                    }
                  }

                  return (
                    <div
                      key={filename}
                      className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => {
                        if (fileRefs[0]) handleReferenceClick(fileRefs[0]);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-blue-600">{displayIcon}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{displayName}</div>
                            <div className="text-xs text-gray-500">{displayDescription}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          Click to view
                        </div>
                      </div>
                    </div>
                  );
                })
                )}
              </div>
            </div>

            {/* NCBI Scientific Sources */}
            {linkedSources.length > 0 && (
              <div className="space-y-3 mt-6">
                <div className="grid gap-3">
                  {linkedSources.map((source, index) => (
                    <div
                      key={source.id || index}
                      className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 hover:bg-neutral-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">

                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{source.article_title}</div>
                            <div className="text-xs text-gray-600 mt-1">{source.article_author}</div>
                            {source.article_link && (
                              <a
                                href={source.article_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary-500 hover:text-primary-600 mt-1 inline-block"
                              >
                                View on NCBI →
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          NCBI Source
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : linkedSources.length > 0 ? (
          <div className="space-y-4">
            {/* Only NCBI Scientific Sources when no file references */}
            <div className="space-y-3">
              <div className="grid gap-3">
                {linkedSources.map((source, index) => (
                  <div
                    key={source.id || index}
                    className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{source.article_title}</div>
                          <div className="text-xs text-gray-600 mt-1">{source.article_author}</div>
                          {source.article_link && (
                            <a
                              href={source.article_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-500 hover:text-primary-600 mt-1 inline-block"
                            >
                              View on NCBI →
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        NCBI Source
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : citedKnowledgeBaseSources.length === 0 && availableKnowledgeBaseSources.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4 flex items-center justify-center bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto"> <BookOpenIcon className="w-10 h-10" /></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No References Found</h3>
            <p className="text-gray-500">This article doesn't contain any file references yet.</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
